import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Dialog,
  type DialogProps,
  DialogContent,
  DialogTitle,
  IconButton,
  styled,
} from "@mui/material";
import { rem } from "polished";
import React, { type ReactElement } from "react";

import { ariaLabel } from "src/constants";
import { useDeviceWidth } from "src/hooks";

import { Icon, type IconName } from "../Icon";

const StyledModal = styled(Dialog)`
  .MuiDialog-paper {
    min-width: 850px;
    min-height: 350px;

    ${(props) => props.theme.breakpoints.down("md")} {
      min-width: unset;
      min-height: unset;
      height: calc(100vh - 4rem);
    }

    ${(props) => props.theme.breakpoints.down("sm")} {
      padding-bottom: ${({ theme }) => theme.spacing(8)};
    }
  }

  .MuiDialog-paperScrollPaper {
    display: initial;

    ${(props) => props.theme.breakpoints.down("md")} {
      margin: ${({ theme }) => theme.spacing(4)};
      border-radius: ${rem(16)};
      align-self: flex-start;
    }
  }
  h2.MuiDialogTitle-root {
    text-align: center;
    color: ${(props) => props.theme.palette.primary.main};
    font-size: ${rem(30)};

    ${(props) => props.theme.breakpoints.down("sm")} {
      font-size: ${rem(20)};
      font-weight: bold;
      text-transform: uppercase;
      display: table-caption;
    }
  }

  .MuiDialogContent-root {
    ${(props) => props.theme.breakpoints.down("md")} {
      padding: ${({ theme }) => theme.spacing(1)} ${({ theme }) => theme.spacing(2)};
    }
  }
`;

const StyledIcon = styled(Icon)`
  width: 100%;
  font-size: ${rem(60)};
  color: ${(props) => props.theme.palette.primary.main};

  ${(props) => props.theme.breakpoints.down("md")} {
    width: ${rem(50)};
  }
`;

const StyledDialogHeader = styled("div")`
  ${(props) => props.theme.breakpoints.down("md")} {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
  }
`;

interface Props extends Omit<DialogProps, "title"> {
  icon?: IconName | null;
  title?: ReactElement | string | null;
  open: boolean;
  hideCloseButton?: boolean;
  disableBackdropClick?: boolean;
  handleClose: () => void;
}

export const Modal = ({
  icon,
  title,
  open,
  hideCloseButton,
  disableBackdropClick,
  handleClose,
  children,
  ...dialogProps
}: Props) => {
  const { isMobile } = useDeviceWidth();

  return (
    <StyledModal
      open={open}
      onClose={(_event, reason) => {
        if (reason === "backdropClick" && disableBackdropClick) {return;}

        handleClose();
      }}
      aria-labelledby="form-dialog-title"
      fullScreen={isMobile}
      {...dialogProps}
    >
      <Box sx={{ mt: hideCloseButton && (title || icon) ? 4 : 0 }}>
        <Box
          sx={{
            display: hideCloseButton ? "none" : "flex",
            justifyContent: "flex-end",
            px: 0.5,
            py: 1,
          }}
        >
          <IconButton aria-label={ariaLabel.closeModal} onClick={handleClose} color="inherit">
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {isMobile ? (
        <StyledDialogHeader>
          {icon && <StyledIcon name={icon} size={50} />}
          {title && (
            <DialogTitle id="form-dialog-title" display="flex">
              {title}
            </DialogTitle>
          )}
        </StyledDialogHeader>
      ) : (
        <StyledDialogHeader>
          {icon && <StyledIcon name={icon} size={50} />}
          {title && <DialogTitle id="form-dialog-title">{title}</DialogTitle>}
        </StyledDialogHeader>
      )}

      <DialogContent>{children}</DialogContent>
    </StyledModal>
  );
};
